<template>
    <div>
        <DCard padding>
            <DRow>
                <DCol>
                    <p class="border-bottom pb-3">
                        <strong>{{ __('tools.partners.compensation_rates.credit_note_type') }}: </strong>
                        <DTag
                            v-if="compensationRates.creditNoteTypePartner !== null"
                            color="success"
                            :label="compensationRates.creditNoteTypePartner.credit_note_type.name"
                        />
                        <DTag
                            v-else
                            color="danger"
                            :label="__('tools.partners.compensation_rates.not_set')"
                        />
                    </p>
                </DCol>
            </DRow>
            <DRow
                class="mb-3"
                align-vertical="end"
            >
                <DCol sm="2">
                    <DInput
                        id="product-group"
                        v-model="filters.productGroup"
                        :label="__('tools.partners.compensation_rates.product_group')"
                        @keydown.enter.exact.prevent="filter(compensationRates.current_page, filters)"
                    />
                </DCol>
                <DCol sm="2">
                    <DInput
                        id="distributor-id"
                        v-model="filters.distributorId"
                        :label="__('tools.partners.compensation_rates.distributor_id')"
                        type="number"
                        pattern="^\d*$"
                        min="1"
                        @keydown.enter.exact.prevent="filter(compensationRates.current_page, filters)"
                    />
                </DCol>
                <DCol sm="1">
                    <DSelect
                        id="active"
                        v-model="filters.active"
                        :label="__('tools.partners.compensation_rates.active')"
                        :options="activeOptions"
                    />
                </DCol>
                <DCol sm="2">
                    <DDateFormatInput
                        id="date-from"
                        v-model="filters.validFrom"
                        format="YYYY-MM-DD"
                        type="date"
                        :label="__('tools.partners.compensation_rates.valid_from')"
                        @keydown.enter.exact.prevent="filter(compensationRates.current_page, filters)"
                    />
                </DCol>
                <DCol sm="2">
                    <DDateFormatInput
                        id="date-to"
                        v-model="filters.validTo"
                        format="YYYY-MM-DD"
                        type="date"
                        :label="__('tools.partners.compensation_rates.valid_to')"
                        @keydown.enter.exact.prevent="filter(compensationRates.current_page, filters)"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="filter(compensationRates.current_page, filters)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DRow>
                <DCol>
                    <DTable
                        class="mt-3"
                        :items="compensationRates.items"
                        :fields="fields"
                    >
                        <template #compensation_rate="{item}">
                            <td>
                                {{ item.compensation_rate }}%
                            </td>
                        </template>
                        <template #active="{item}">
                            <td>
                                <DTag
                                    v-if="item.active"
                                    color="success"
                                    :label="__('tools.partners.compensation_rates.active')"
                                />
                                <DTag
                                    v-else
                                    color="danger"
                                    :label="__('tools.partners.compensation_rates.inactive')"
                                />
                            </td>
                        </template>
                        <template #valid_to="{item}">
                            <td>
                                {{ setDateFormat(item.valid_to) }}
                            </td>
                        </template>
                        <template #valid_from="{item}">
                            <td>
                                {{ setDateFormat(item.valid_from) }}
                            </td>
                        </template>
                        <template #actions="{item}">
                            <td>
                                <div style="display: flex; grid-gap: 10px; justify-content: end;">
                                    <DButton
                                        v-if="item.canBeEdited"
                                        icon="pencil"
                                        mini
                                        @click="$inertia.get(editRecord(item.id))"
                                    />
                                    <DButton
                                        v-if="item.canBeDeleted"
                                        icon="trash-can-outline"
                                        color="danger"
                                        mini
                                        :disabled="isInLoadingState"
                                        @click="deleteRecord(item.id)"
                                    />
                                </div>
                            </td>
                        </template>
                    </DTable>
                    <DPagination
                        v-if="compensationRates.last_page > 1"
                        :active-page="compensationRates.current_page"
                        :pages="compensationRates.last_page"
                        align="center"
                        @update:activePage="changePage"
                    />
                </DCol>
            </DRow>
            <DFabs>
                <DFab
                    id="btn-edit-credit-note-type"
                    icon="pencil"
                    color="secondary"
                    :label="__('tools.partners.compensation_rates.credit_note_type')"
                    @click="creditNoteTypeModal"
                />
                <DFab
                    id="btn-add"
                    icon="plus"
                    :label="__('tools.partners.compensation_rates.add_compensation_rate')"
                    @click="$inertia.get($route('tools.partners.partner-compensation-rates.create'))"
                />
            </DFabs>
            <DModal
                v-model:show="showConfirmationModal"
                :title="__('tools.partners.compensation_rates.modals.delete_confirmation.title')"
                :message="__('tools.partners.compensation_rates.modals.delete_confirmation.message')"
                is-confirm
                is-error
                @close="closeModal"
                @confirm="deleteCallBack"
            />
            <DModal
                v-model:show="showCreditNoteTypeModal"
                :title="__('tools.partners.compensation_rates.modals.credit_note_type.title')"
                is-confirm
                :is-awaiting-validation="true"
                @close="closeCreditNoteTypeModal"
                @confirm="postCallBack"
            >
                <DCard padding>
                    <DRow>
                        <DCol>
                            <DSelect
                                id="credit_note_type"
                                v-model="creditNoteTypeId"
                                :label="__('tools.partners.compensation_rates.modals.credit_note_type.message')"
                                :options="creditNoteTypeOptions"
                            />
                        </DCol>
                    </DRow>
                </DCard>
            </DModal>
        </DCard>
    </div>
</template>

<script>

export default {
    name: 'CompensationRateIndex',
    props: {
        compensationRates: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            fields: [
                { key: 'product_group', label: this.__('tools.partners.compensation_rates.table.product_group') },
                { key: 'distributor_id', label: this.__('tools.partners.compensation_rates.table.distributor_id') },
                { key: 'compensation_rate', label: this.__('tools.partners.compensation_rates.table.compensation_rate') },
                { key: 'valid_from', label: this.__('tools.partners.compensation_rates.table.valid_from') },
                { key: 'valid_to', label: this.__('tools.partners.compensation_rates.table.valid_to') },
                { key: 'active', label: this.__('tools.partners.compensation_rates.table.active') },
                { key: 'actions', label: '' }
            ],
            creditNoteTypeId: this.compensationRates.creditNoteTypePartner?.credit_note_type.id,
            filters: this.compensationRates.filters,
            showConfirmationModal: false,
            showCreditNoteTypeModal: false,
            deleteCallBack: () => { return {} },
            postCallBack: () => { return {} }
        }
    },
    computed: {
        activeOptions () {
            const activeOptions = []
            activeOptions.push({ value: '', label: '' })
            activeOptions.push({ value: '1', label: this.__('tools.partners.compensation_rates.active') })
            activeOptions.push({ value: '0', label: this.__('tools.partners.compensation_rates.inactive') })

            return activeOptions
        },
        creditNoteTypeOptions () {
            const creditNoteTypeOptions = this.compensationRates.creditNoteTypeList.map(creditNoteType => {
                return {
                    value: creditNoteType.id,
                    label: creditNoteType.name
                }
            })

            creditNoteTypeOptions.unshift({
                value: '',
                label: '',
                hidden: 'hidden'
            })

            return creditNoteTypeOptions
        }
    },
    methods: {
        editRecord (id) {
            return this.$route('tools.partners.partner-compensation-rates.edit', {
                partner_compensation_rate: id
            })
        },
        deleteRecord (id) {
            this.deleteCallBack = () => {
                this.$inertia.delete(
                    this.$route('tools.partners.partner-compensation-rates.destroy', {
                        partner_compensation_rate: id
                    }),
                    {
                        preserveScroll: true
                    }
                )
                this.showConfirmationModal = false
            }
            this.showConfirmationModal = true
        },
        closeModal () {
            this.showConfirmationModal = false
            this.deleteCallBack = () => { return {} }
        },
        changePage (page) {
            const queryParameters = {}

            if (page > 1) {
                queryParameters.page = page
            }

            this.$inertia.get(this.$route('tools.partners.partner-compensation-rates.index'), queryParameters)
        },
        filter (page, filters) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))

            if (page > 1) {
                queryParameters.page = page
            }

            this.$inertia.get(this.$route('tools.partners.partner-compensation-rates.index', {
                saveFilters: true
            }), queryParameters)
        },
        resetFilters () {
            this.$inertia.get(this.$route('tools.partners.partner-compensation-rates.index', {
                saveFilters: true,
                defaultFilters: true
            }))
        },
        creditNoteTypeModal () {
            this.postCallBack = () => {
                this.$inertia.post(
                    this.$route('tools.partners.store-credit-note-type', {
                        credit_note_type_id: this.creditNoteTypeId
                    }),
                    {
                        preserveScroll: true
                    }
                )
                this.showCreditNoteTypeModal = false
            }
            this.showCreditNoteTypeModal = true
        },
        closeCreditNoteTypeModal () {
            this.showCreditNoteTypeModal = false
            this.postCallBack = () => { return {} }
        }
    }
}
</script>
